import { sankeyLinkHorizontal, sankey } from 'd3-sankey';
import { SankeyLink, SankeyNode } from 'd3-sankey';
import { select } from 'd3-selection';
import { Selection as D3Selection, BaseType } from 'd3-selection';
import React, { useEffect, useRef, Dispatch, SetStateAction, useContext } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { AlertIcon } from '@/ui/Icons';

import { Route } from '@/utils/0x.types';

import { classNames } from '../../utils/classnames';
import { transformRouteDataD3, sankeyHoverOpacityLevels, applyOpacityEffects } from './helpers';
import {
  diagramContainerClass,
  hiddenNodeClass,
  nodeClass,
  nodeTextClass,
  sankeySvgContainerClass,
  tooltipContentClass,
} from './index.css';
import { SankeyContext } from './SankeyProvider';
import SankeyTooltip from './SankeyTooltip';
import { LinkD3Custom, NodeD3Custom, SubRoute } from './types';

function curvyLine(d: LinkD3Custom, i: any) {
  const height = d.source.y1 - d.source.y0;

  const startY = d.source.y0 + (d.source.y1 - d.source.y0) / 2;
  const endY = d.target.y0 + (d.target.y1 - d.target.y0) / 2;

  const delta = d.target.x0 - d.source.x1;

  const handleX = delta / 4;

  const middlePointX = d.source.x1 + delta / 2;
  const offsetY = startY + Math.min(height / 5, 90);
  return `M ${d.source.x1} ${startY}
  C ${d.source.x1 + handleX} ${startY} ${middlePointX - handleX} ${offsetY} ${middlePointX} ${offsetY} 
  S${d.target.x0 - handleX} ${endY} ${d.target.x0} ${endY}`;
}

interface SankeyChartProps {
  route: Route;
  chainId: number;
  width: number;
  height: number;
  isMobile: boolean;
  onSubRouteTouch: Dispatch<SetStateAction<SubRoute | undefined>>;
  isPreview?: boolean;
  className?: string;
}
const hiddenNodesMarginSize = 4;
const logoWithMarginHeight = 56;
export const TOOLTIP_WIDTH = 235;
export const TOOLTIP_HEIGHT = 70;

export const SankeyDiagram: React.FC<SankeyChartProps> = ({
  route,
  chainId,
  width,
  height,
  isMobile,
  onSubRouteTouch,
  isPreview = false,
  className,
}) => {
  const sankeyRef = useRef<HTMLDivElement | null>(null);
  const { liquiditySourceOnHoverInLegend } = useContext(SankeyContext);

  useEffect(() => {
    if (!sankeyRef.current) return;
    const data = transformRouteDataD3({ route, chainId });
    let selectedSubRoute: SubRoute | undefined;
    const svg = select(sankeyRef.current)
      .selectAll('svg')
      .data([null])
      .join('svg')
      .attr('width', width)
      .attr('height', height + (isPreview ? 0 : logoWithMarginHeight))
      .attr('aria-label', '0x sankey diagram of the current trade route')
      .attr('viewBox', [0, 0, width, height])
      .attr('class', sankeySvgContainerClass);

    // Calculate logo dimensions
    const logoHeight = 24;
    const logoWidth = 108;
    const cupLogoWidth = 24;
    const logoScale = 1.2; // Adjust this to scale the logos
    const scaledLogoHeight = logoHeight * logoScale;
    const scaledLogoWidth = logoWidth * logoScale;
    const scaledCupLogoWidth = cupLogoWidth * logoScale;
    if (!isPreview) {
      const logoGroup = svg
        .append('g')
        .attr(
          'transform',
          `translate(${width - scaledLogoWidth - scaledCupLogoWidth}, ${height - scaledLogoHeight / 2}) scale(${logoScale})`,
        );

      logoGroup.html(`
    <g opacity="0.2">
      <path d="M90.7245 5.90543C89.7926 5.90543 89.0389 6.12707 88.418 6.63667H88.2625V1.5625H85.8452V17.0948H88.218L88.2625 10.4918C88.2625 9.29548 89.3044 8.31989 90.4581 8.31989C91.6781 8.31989 92.6536 9.29457 92.6536 10.4918V17.0948H95.0265V10.4918C95.0265 7.96562 92.9864 5.90543 90.7245 5.90543ZM102.257 15.0564C100.349 15.0564 98.7748 13.5494 98.7748 11.6218C98.7748 9.69425 100.349 8.16546 102.257 8.16546C104.164 8.16546 105.694 9.67245 105.694 11.6218C105.694 13.5712 104.186 15.0564 102.257 15.0564ZM102.257 5.86092C99.0857 5.86092 96.4911 8.45342 96.4911 11.6218C96.4911 14.7902 98.9748 17.3609 102.079 17.3609C103.477 17.3609 104.851 16.8731 105.85 15.8321H106.005V17.0948H108.001V11.6218C108.001 8.45342 105.406 5.86092 102.257 5.86092ZM57.9704 15.0564C56.063 15.0564 54.4884 13.5494 54.4884 11.6218C54.4884 9.69425 56.063 8.16546 57.9704 8.16546C59.8777 8.16546 61.4078 9.67245 61.4078 11.6218C61.4078 13.5712 59.8995 15.0564 57.9704 15.0564ZM69.0363 8.43071H71.2536V6.01534H69.0363V2.42636H66.6189V6.01625H64.867V8.43161H66.6189V12.6864C66.6189 14.7911 68.2153 17.3618 71.2536 17.3618V14.9465C69.0799 14.9465 69.0363 12.7309 69.0363 12.7309V8.43252V8.43071ZM45.9508 5.90452C44.6198 5.90452 43.3779 6.48043 42.6024 7.47782H42.4697C41.6933 6.48043 40.4514 5.90452 39.1432 5.90452C36.6149 5.90452 34.5303 7.96562 34.5303 10.4909V17.0939H36.9249V10.4918C36.9249 9.29548 37.9004 8.31989 39.1423 8.31989C40.3842 8.31989 41.3597 9.29457 41.3597 10.4918V17.0948H43.7543V10.4918C43.7543 9.29548 44.7298 8.31989 45.9498 8.31989C47.1699 8.31989 48.1899 9.29457 48.1899 10.4918V17.0948H50.5628V10.4918C50.5628 7.96562 48.5 5.90543 45.9498 5.90543L45.9508 5.90452ZM57.9704 5.86001C54.7993 5.86001 52.2047 8.45251 52.2047 11.6209C52.2047 14.7893 54.6884 17.36 57.7931 17.36C59.1904 17.36 60.565 16.8722 61.585 15.8312H61.7405V17.0939H63.7142V11.6209C63.7142 8.408 61.1196 5.86001 57.9704 5.86001ZM78.5949 14.9446C76.732 14.9446 75.202 13.4822 75.202 11.6209C75.202 9.75966 76.732 8.27537 78.5949 8.27537C79.9258 8.27537 81.0786 9.05113 81.6332 10.1584H84.1387C83.5178 7.67676 81.2559 5.86001 78.5949 5.86001C75.3793 5.86001 72.8064 8.45251 72.8064 11.6209C72.8064 14.7893 75.3793 17.36 78.5949 17.36C81.2559 17.36 83.5178 15.477 84.1387 12.9953H81.6332C81.0786 14.1698 79.9258 14.9456 78.5949 14.9456V14.9446Z" fill="black"/>
      <path d="M17.8076 2.6688C17.7639 2.756 17.6594 2.79325 17.5703 2.75328C16.2457 2.15103 14.6356 1.94755 13.5774 1.94755C9.77268 1.94755 5.96706 3.80154 5.96706 6.84913C5.96706 8.48329 7.19348 9.86584 8.98628 9.86584C10.9673 9.86584 12.4455 8.42062 13.6401 7.10075C14.7092 5.90715 15.9675 4.58728 17.7594 4.58728C19.7095 4.58728 21.1241 6.0325 21.1241 7.98096C21.1241 11.0603 17.413 14.5167 12.2873 14.5167C7.16166 14.5167 3.4515 11.7825 3.4515 7.98096C3.4515 4.43013 7.66531 1.22539 12.2882 1.22539C14.6474 1.22539 16.4239 1.71955 17.7312 2.42808C17.8176 2.47532 17.8512 2.5816 17.8076 2.66971V2.6688ZM12.2882 0C5.96706 0 0.80957 5.1532 0.80957 11.4691C0.80957 17.785 5.96706 23.0009 12.2882 23.0009C18.6094 23.0009 23.8296 17.8477 23.8296 11.4691C23.8296 5.09052 18.6712 0 12.2882 0Z" fill="#17171C"/>
    </g>
  `);
    }

    // If the data is not valid, render an error message
    if (!data.isValid) {
      const iconSize = 48;
      const iconX = width / 2 - iconSize / 2;
      const iconY = height / 2 - iconSize - 16;
      svg
        .append('g')
        .attr('transform', `translate(${iconX}, ${iconY})`)
        .html(
          renderToStaticMarkup(
            <AlertIcon width={iconSize} height={iconSize} style={{ color: 'rgba(0,0,0,0.08)' }} />,
          ),
        );

      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2 + iconSize / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '32px')
        .attr('fill', 'rgba(0,0,0,0.08)')
        .text('There was an issue loading the route diagram.');

      return;
    }

    const isNonMultiplexed = data.nodes.length === 2 && data.links.length === 1;

    const margin = {
      top: isPreview ? 20 : 40,
      right: isPreview ? 20 : 40,
      bottom: isPreview ? 20 : 40,
      left: isPreview ? 20 : 40,
    };
    // if we are in a single hop setting, scale down further
    if (isNonMultiplexed) {
      if (isPreview) {
        margin.top = 60;
        margin.bottom = 60;
      } else {
        margin.top = 300;
        margin.bottom = 300;
      }
    }
    const sankeyLayout = sankey<NodeD3Custom, LinkD3Custom>()
      .nodeId((d: NodeD3Custom) => d.id)
      .nodeWidth(width / 12)
      .nodePadding(isPreview ? 12 : 24)
      .extent([
        [margin.left, margin.top],
        [width - margin.right, height - margin.bottom],
      ]);

    // after this generator executes, each links source and target values are updated from string to NodeD3Custom objects
    const { nodes, links } = sankeyLayout({
      nodes: data.nodes,
      links: data.links,
    });

    for (const node of nodes) {
      if (!node.hide) continue;
      const incoming = node.sourceLinks[0];
      if (!incoming) {
        continue;
      }
      node.height = incoming.width || 2;
    }

    sankeyLayout.update({ links, nodes });

    select(sankeyRef.current).select('#sankeyLinks').remove();
    select(sankeyRef.current).select('#sankeyNodeGroup').remove();

    const svgDefs = svg.append('defs');

    const minNodeHeight = isPreview ? 14 : 36;

    if (!isPreview) {
      // Define a 8px dot pattern for the svg background
      const dotPattern = svgDefs
        .append('pattern')
        .attr('id', 'bgDotPattern')
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', 8)
        .attr('height', 8);
      dotPattern.append('circle').attr('cx', 4).attr('r', 1).attr('fill', 'rgba(0,0,0,0.08)');
      svg
        .append('rect')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('fill', 'url(#bgDotPattern)');
    }

    //Define a box-shadow-25 filter for non hidden nodes
    svgDefs.append('filter').attr('id', 'box-shadow-25').html(`
        <feDropShadow dx="0" dy="4" stdDeviation="18" flood-opacity="0.06"/>
    `);

    links.forEach((link: LinkD3Custom) => {
      const gradient = svgDefs
        .append('linearGradient')
        .attr('id', `gradient-${link.pathkey}`)
        .attr('gradientUnits', 'userSpaceOnUse');
      gradient.append('stop').attr('offset', '0%').attr('stop-color', link.gradientStart);

      gradient.append('stop').attr('offset', '100%').attr('stop-color', link.gradientEnd);
    });

    const nodePadding = 20;

    const minimumLinkWidth = 2;

    nodes.forEach((node: NodeD3Custom) => {
      if (node.hide) {
        node.height = Math.max(node.height || minimumLinkWidth, minimumLinkWidth);
      } else {
        node.height = Math.max(node.y1 - node.y0 + nodePadding, minNodeHeight);
      }
    });

    const tooltipContainer = select(sankeyRef.current).select('#tooltipContainer');
    const tooltipContentContainer = select(sankeyRef.current).select('#tooltipContent');
    /**
     * The sankeyLinkGroup will contain a group of path elements for each link.
     * The path element is created using the sankeyLinkHorizontal generator and
     * each path in the entire group can uniquely be identified by it's pathKey property.
     * The pathKey is used to connect links with hidden nodes and to apply hover effects
     * when the user hovers over a hidden-node or link which pertains to a particular sub route.
     */
    const linkGroup = svg
      .append('g')
      .attr('id', 'sankeyLinkGroup')
      .attr('fill', 'none')
      .attr('stroke-opacity', 0.5)
      .selectAll('g')
      .data(links)
      .join('g');

    const path = sankeyLinkHorizontal<NodeD3Custom, LinkD3Custom>();

    // sort the nodes so hidden nodes are rendered first.
    // this is necessary as the z positioning is determined by the drawing order in SVG
    nodes.sort((a, b) => {
      if ((a.hide && b.hide) || (!a.hide && !b.hide)) return 0;
      if (a.hide && !b.hide) return -1;
      return 1;
    });

    /**
     * The `sankeyNodeGroup` will contain a group of svg elements for each node in the sankey diagram.
     * Each group in the nodeGroup will have a datum type of NodeD3Custom which contains a `hide` attribute
     * which determines if the node should render as a "hidden" node or not.
     *
     * If the node is "hidden" (i.e. hide=true), the node's group will be contain a `rect` element with a gradient
     * background & empty text & image elements. Each hidden node's group will be identified by it's `pathKey` in the DOM
     * and is used to apply effects on users touch or hover.
     *
     * For nodes which are not hidden, the node's group contain a `rect` with a white background and include text and image elements
     * which render the node's token and symbol.
     *
     */
    const nodeGroup = svg
      .append('g')
      .attr('id', 'sankeyNodeGroup')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('id', (d: NodeD3Custom) => (d.hide && d.pathkey ? d.pathkey : d.symbol));

    linkGroup
      .append('path')
      .attr('d', isNonMultiplexed ? curvyLine : path)
      .attr('id', (d: LinkD3Custom) => d.pathkey)
      .attr('data-source', (d: LinkD3Custom) => d.liquiditySource)
      .attr('stroke', (d: LinkD3Custom) => `url(#gradient-${d.pathkey})`)
      .attr('stroke-opacity', sankeyHoverOpacityLevels.default)
      .attr('y', (d: LinkD3Custom) => d.y0 + 2)
      .attr('stroke-width', (d: LinkD3Custom) => {
        return Math.max(d.width, 2);
      })
      .on('mousemove', function (event: MouseEvent | TouchEvent, d: LinkD3Custom) {
        if (sankeyRef.current === null) return;
        const currentSubRoute: SubRoute = {
          sourceLink: d,
          value: d.value,
          liquiditySource: d.liquiditySource,
        };
        handleOnMouseOrTouchEvent({
          event,
          pathKey: d.pathkey,
          currentSubRoute,
          linkGroup,
          nodeGroup,
          sankeyRef,
          tooltipContainer,
          tooltipContentContainer,
          selectedSubRoute,
          isMobile,
          onSubRouteTouch,
        });
      })
      .on('mouseout', function (event: MouseEvent, d: LinkD3Custom) {
        if (isMobile) onSubRouteTouch(undefined);
        applyOpacityEffects(
          linkGroup,
          nodeGroup,
          'exit',
          d.pathkey,
          liquiditySourceOnHoverInLegend,
        );
        tooltipContainer.style('display', 'none');
      });

    nodeGroup
      .append('rect')
      .attr('data-hidden', (d: NodeD3Custom) => d.hide)
      .attr('id', (d: NodeD3Custom) => (d.hide && d.pathkey ? d.pathkey : d.symbol))
      .attr('data-source', (d: NodeD3Custom) =>
        d.hide && d.liquiditySource ? d.liquiditySource : '',
      )
      .attr('class', (d: NodeD3Custom) => (d.hide ? hiddenNodeClass : nodeClass))
      //filter and 1px inset stroke help complete the Shadow25 effect
      .attr('filter', (d: NodeD3Custom) => (d.hide ? '' : 'url(#box-shadow-25)'))
      .attr('stroke', (d: NodeD3Custom) => (d.hide ? 'transparent' : 'rgba(0,0,0,0.06)'))
      .attr('stroke-width', isPreview ? '1' : '2')
      .attr('stroke-alignment', 'inner')
      .attr('id', (d: NodeD3Custom) => (d.hide && d.pathkey ? d.pathkey : d.symbol))
      .attr('rx', (d: NodeD3Custom) => (d.hide ? '0' : isPreview ? '8' : '20'))
      .attr('x', (d: NodeD3Custom) => Math.floor(d.x0))
      .attr('y', (d: NodeD3Custom) => {
        if (d.hide) {
          // if we have the minimal height, no need to offset
          if (d.height === 2) return d.y0 - 1;
          return d.y0;
        }
        return d.y0 - nodePadding / 2;
      })
      .attr('height', (d: NodeD3Custom) => d.height || d.y1 - d.y0)
      .attr('width', (d: NodeD3Custom) => Math.floor(Math.ceil(d.x1) - Math.floor(d.x0)))
      .attr('fill', (d: NodeD3Custom) => (d.hide ? `url(#gradient-${d.pathkey})` : 'white'))
      .attr('fill-opacity', (d: NodeD3Custom) => (d.hide ? sankeyHoverOpacityLevels.default : 1))
      .on('mousemove', function (event: MouseEvent | TouchEvent, d: NodeD3Custom) {
        if (!d.hide && d.symbol.length > 4) {
          tooltipContentContainer.html(`<span>${d.symbol}</span>`);
          return;
        }

        if (!d.hide || !sankeyRef.current || !d.pathkey) return;
        const currentSubRoute: SubRoute = {
          sourceLink: d.sourceLinks[0],
          value: d.sourceLinks[0].value,
          liquiditySource: d.sourceLinks[0].liquiditySource,
        };
        handleOnMouseOrTouchEvent({
          event,
          pathKey: d.pathkey,
          currentSubRoute,
          linkGroup,
          nodeGroup,
          sankeyRef,
          tooltipContainer,
          tooltipContentContainer,
          selectedSubRoute,
          isMobile,
          onSubRouteTouch,
        });

        if (isMobile) {
          onSubRouteTouch(selectedSubRoute);
          return;
        }
        if (currentSubRoute.sourceLink.pathkey !== selectedSubRoute?.sourceLink.pathkey) {
          selectedSubRoute = currentSubRoute;
          tooltipContentContainer.html(
            `${renderToStaticMarkup(<SankeyTooltip {...selectedSubRoute} />)}`,
          );
        }
        const svgRect = sankeyRef.current.getBoundingClientRect();
        let tooltipX, tooltipY;

        if (event.type === 'touchmove' || event.type === 'touchstart') {
          const touchEvent = event as TouchEvent;
          tooltipX = touchEvent.touches[0].pageX - svgRect.left;
          tooltipY = touchEvent.touches[0].pageY - svgRect.top;
        } else {
          const mouseEvent = event as MouseEvent;
          tooltipX = mouseEvent.pageX - svgRect.left;
          tooltipY = mouseEvent.pageY - svgRect.top;
        }
        const toolTipOverflowsOnRight = tooltipX + TOOLTIP_WIDTH > svgRect.width;
        const toolTipOverflowsOnBottom = tooltipY + TOOLTIP_HEIGHT > svgRect.height;
        if (toolTipOverflowsOnRight) {
          tooltipX = tooltipX - TOOLTIP_WIDTH;
        }
        if (toolTipOverflowsOnBottom) {
          tooltipY = tooltipY - TOOLTIP_HEIGHT;
        }
        tooltipContainer
          .style('display', 'block')
          .style('transform', `translate(${tooltipX}px, ${tooltipY}px)`);
      })
      .on('mouseout', function (event: MouseEvent, d: NodeD3Custom) {
        if (!d.hide || !sankeyRef.current || !d.pathkey) return;
        if (isMobile) onSubRouteTouch(undefined);
        applyOpacityEffects(
          linkGroup,
          nodeGroup,
          'exit',
          d.pathkey,
          liquiditySourceOnHoverInLegend,
        );
        tooltipContainer.style('display', 'none');
      });

    const tokenImageSize = 34;
    const characterWidth = 14;
    const imagePadding = 12;
    if (!isPreview) {
      nodeGroup
        .append('text')
        .attr('class', nodeTextClass)
        .attr('id', (d: NodeD3Custom) => (d.hide && d.pathkey ? d.pathkey : ''))
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'start')
        .attr('pointer-events', 'none')
        .html((d: NodeD3Custom) =>
          d.hide ? ' ' : d.symbol.length > 4 ? `${d.symbol.slice(0, 3)}..` : d.symbol,
        )
        .attr('x', function (this: SVGTextElement, d: NodeD3Custom) {
          const rectWidth = Math.floor(Math.ceil(d.x1) - Math.floor(d.x0));
          return (
            Math.floor(d.x0) +
            (rectWidth -
              tokenImageSize -
              imagePadding -
              characterWidth * Math.min(d.symbol.length, 4)) /
              2 +
            tokenImageSize +
            imagePadding
          );
        })
        .attr('y', function (d: NodeD3Custom) {
          if (d.hide) return d.y0;
          const nodeHeight = d.y1 - d.y0;
          const adjustedHeight = Math.max(nodeHeight, minNodeHeight);
          return d.y0 + adjustedHeight / 2;
        })
        .style('font-size', '24px');
    }

    const imgSize = isPreview ? 12 : tokenImageSize;

    nodeGroup
      .append('image')
      .attr('dominant-baseline', 'middle')
      .attr('href', (d: NodeD3Custom) => d.logoUrl)
      .attr('width', (d: NodeD3Custom) => (d.hide ? 0 : imgSize))
      .attr('height', (d: NodeD3Custom) => (d.hide ? 0 : imgSize))
      .each(function (this: SVGImageElement, d: NodeD3Custom) {
        const image = select(this);
        image.on('error', function () {
          image.attr('href', '/images/liquidity-providers/default.png');
        });
      })
      .attr('clip-path', 'circle(50% at 50% 50%)')
      .attr('x', function (d: NodeD3Custom) {
        const padding = isPreview ? 0 : imagePadding;
        const rectWidth = Math.floor(Math.ceil(d.x1) - Math.floor(d.x0));
        const textWidth = isPreview ? 0 : characterWidth * Math.min(d.symbol.length, 4);
        const totalWidth = imgSize + padding + textWidth;
        return Math.floor(d.x0) + (rectWidth - totalWidth) / 2;
      })
      .attr('y', function (d: NodeD3Custom) {
        if (d.hide) return d.y0 + hiddenNodesMarginSize / 2;
        const nodeHeight = d.height || d.y1 - d.y0;
        return d.y0 - nodePadding / 2 + (nodeHeight - imgSize) / 2;
      });
    /**
     * Apply opacity effects to the sankey diagram when the user
     * hovers over a liquidity source in the <SankeyLegend/>.
     */
    if (liquiditySourceOnHoverInLegend) {
      applyOpacityEffects(linkGroup, nodeGroup, 'enter', undefined, liquiditySourceOnHoverInLegend);
    } else {
      applyOpacityEffects(linkGroup, nodeGroup, 'exit', undefined, liquiditySourceOnHoverInLegend);
    }
    const currentSankeyRef = sankeyRef.current;
    return () => {
      select(currentSankeyRef).select('svg').remove();
    };
  }, [
    route,
    width,
    height,
    chainId,
    isMobile,
    onSubRouteTouch,
    isPreview,
    liquiditySourceOnHoverInLegend,
  ]);

  return (
    <div ref={sankeyRef} className={classNames(diagramContainerClass, className)}>
      <div
        id="tooltipContainer"
        style={{ position: 'absolute', pointerEvents: 'none', display: 'none' }}
      >
        <div id="tooltipContent" className={tooltipContentClass}></div>
      </div>
    </div>
  );
};
interface HandleSankeyInteractionParams {
  event: MouseEvent | TouchEvent;
  currentSubRoute: SubRoute;
  pathKey: string;
  linkGroup: D3Selection<
    BaseType | SVGGElement,
    SankeyLink<NodeD3Custom, LinkD3Custom>,
    SVGGElement,
    null
  >;
  nodeGroup: D3Selection<
    BaseType | SVGGElement,
    SankeyNode<NodeD3Custom, LinkD3Custom>,
    SVGGElement,
    null
  >;
  sankeyRef: React.RefObject<HTMLDivElement>;
  tooltipContainer: D3Selection<BaseType, unknown, null, undefined>;
  tooltipContentContainer: D3Selection<BaseType, unknown, null, undefined>;
  selectedSubRoute: SubRoute | undefined;
  isMobile: boolean;
  onSubRouteTouch: (subRoute: SubRoute | undefined) => void;
}
/**
 * Handles the interaction when the user hovers over a node or link in the sankey diagram.
 */
export function handleOnMouseOrTouchEvent({
  event,
  pathKey,
  currentSubRoute,
  linkGroup,
  nodeGroup,
  sankeyRef,
  tooltipContainer,
  tooltipContentContainer,
  selectedSubRoute,
  isMobile,
  onSubRouteTouch,
}: HandleSankeyInteractionParams): void {
  if (sankeyRef.current === null) return;
  if (!pathKey) return;
  applyOpacityEffects(linkGroup, nodeGroup, 'enter', pathKey, undefined);
  if (isMobile) {
    onSubRouteTouch(currentSubRoute);
    return;
  }
  if (currentSubRoute.sourceLink.pathkey !== selectedSubRoute?.sourceLink.pathkey) {
    selectedSubRoute = currentSubRoute;
    tooltipContentContainer.html(`${renderToStaticMarkup(<SankeyTooltip {...currentSubRoute} />)}`);
  }
  const svgRect = sankeyRef.current.getBoundingClientRect();
  let tooltipX, tooltipY;
  if (event.type === 'touchmove' || event.type === 'touchstart') {
    const touchEvent = event as TouchEvent;
    tooltipX = touchEvent.touches[0].pageX - svgRect.left;
    tooltipY = touchEvent.touches[0].pageY - svgRect.top;
  } else {
    const mouseEvent = event as MouseEvent;
    tooltipX = mouseEvent.pageX - svgRect.left;
    tooltipY = mouseEvent.pageY - svgRect.top;
  }
  const toolTipOverflowsOnRight = tooltipX + TOOLTIP_WIDTH > svgRect.width - TOOLTIP_WIDTH;
  const toolTipOverflowsOnBottom = tooltipY + TOOLTIP_HEIGHT > svgRect.height - TOOLTIP_HEIGHT;
  if (toolTipOverflowsOnRight) {
    tooltipX = tooltipX - TOOLTIP_WIDTH;
  }
  if (toolTipOverflowsOnBottom) {
    tooltipY = tooltipY - TOOLTIP_HEIGHT;
  }
  tooltipContainer
    .style('display', 'block')
    .style('transform', `translate(${tooltipX}px, ${tooltipY}px)`);
}
